import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { apiService, User, Profile, Session, BackgroundJobStatus } from '../services/api';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  backgroundJobStatus: BackgroundJobStatus | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [backgroundJobStatus, setBackgroundJobStatus] = useState<BackgroundJobStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const saveSession = (session: Session) => {
    localStorage.setItem('access_token', session.access_token);
    localStorage.setItem('refresh_token', session.refresh_token);
  };

  const clearSession = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setProfile(null);
    setBackgroundJobStatus(null);
  }, []);

  const handleSessionExpired = useCallback(() => {
    clearSession();
    setIsLoading(false);
    window.location.replace('/login');
  }, [clearSession]);

  const refreshAuth = useCallback(async () => {
    try {
      const data = await apiService.getCurrentUser();
      setUser(data.user);
      setProfile(data.profile);
      setBackgroundJobStatus(data.background_job_status ?? null);
    } catch (error) {
      console.error('Failed to refresh auth:', error);
      clearSession();
    }
  }, [clearSession]);

  const login = async (email: string, password: string) => {
    try {
      const data = await apiService.login(email, password);
      saveSession(data.session);
      setUser(data.user);
      setProfile(data.profile);
      setBackgroundJobStatus(data.background_job_status ?? null);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    try {
      // Only call the API - don't save session or set user
      // Email verification is required before login
      await apiService.register(email, password, firstName, lastName);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      clearSession();
    }
  };

  // Check if user is already logged in on mount
  useEffect(() => {
    apiService.setUnauthorizedHandler(handleSessionExpired);

    return () => {
      apiService.clearUnauthorizedHandler();
    };
  }, [handleSessionExpired]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          await refreshAuth();
        } catch (error) {
          console.error('Failed to initialize auth:', error);
          clearSession();
        }
      }
      setIsLoading(false);
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSession, refreshAuth]);

  // Set up token refresh interval (every 50 minutes)
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const session = await apiService.refreshToken(refreshToken);
          saveSession(session);
        } catch (error) {
          console.error('Failed to refresh token:', error);
          await logout();
        }
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const value: AuthContextType = {
    user,
    profile,
    backgroundJobStatus,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

